import classNames from 'classnames';
import * as PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import TypesService from '../../services/TypesService';

import './PinCodeInput.scss';

const pinLength = TypesService.PinCodeLength;

const PinCodeInput = ({
	className = '',
	value = '',
	onChange = () => {},
	onFill = () => {},
	success = false,
	error = false,
	disabled = false,
	autoFocus = true,
}) => {
	const pinRef = useRef([]);

	const pinArray = value.split('').concat(new Array(pinLength).fill('')).slice(0, pinLength);

	const focusFirstEmply = () => {
		pinRef.current[Math.min(value.length, pinLength - 1)].focus();
	};

	useEffect(() => {
		if (autoFocus) focusFirstEmply();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (autoFocus && !value) focusFirstEmply();
	}, [value]); // eslint-disable-line react-hooks/exhaustive-deps

	const updateValue = (char, index) => {
		if (char && !/^\d+$/.test(char)) return;

		const newValue = [...pinArray.slice(0, index), char, ...pinArray.slice(index + 1)];
		const strValue = newValue.join('');
		onChange(strValue);

		if (char) {
			pinRef.current[Math.min(strValue.length, pinLength - 1)].focus();

			if (strValue.length === pinLength) onFill(strValue);
		}
	};

	const pinKeyDown = (e, index) => {
		const lastIndex = pinLength - 1;
		if (e.key === 'Backspace' && index > 0 && (index < lastIndex || !pinArray[lastIndex])) {
			if (value[index - 1]) {
				pinRef.current[value.length - 1].focus();
			} else {
				pinRef.current[value.length].focus();
			}
		}
	};

	const pasteValue = e => {
		e.preventDefault();
		const pasteValue = e.clipboardData.getData('text').trim();

		if (!pasteValue || !/^\d+$/.test(pasteValue)) return;

		const newPinValue = (value + pasteValue).slice(0, pinLength);
		onChange(newPinValue);
		pinRef.current[Math.min(newPinValue.length, pinLength - 1)].focus();

		if (newPinValue.length === pinLength) {
			onFill(newPinValue);
		}
	};

	return (
		<div
			className={classNames('PinCodeInput', className, {
				PinCodeInputSuccess: success,
				PinCodeInputError: error,
				PinCodeInputFilled: value.length === pinLength,
			})}
			onClick={focusFirstEmply}
		>
			{pinArray.map((char, index) => (
				<div className='PinCodeInput__field-box' key={index}>
					<input
						ref={el => (pinRef.current[index] = el)}
						className={classNames('PinCodeInput__char-field FormBlock', {
							'PinCodeInput__char-filled-field': char,
						})}
						value={char}
						onChange={e => updateValue(e.target.value, index)}
						maxLength={1}
						disabled={disabled}
						onKeyDown={e => pinKeyDown(e, index)}
						onPaste={pasteValue}
						autoComplete='off'
						autoCorrect='off'
						inputMode='numeric'
					/>
				</div>
			))}
		</div>
	);
};

PinCodeInput.propTypes = {
	className: PropTypes.string,
	value: PropTypes.string,
	onChange: PropTypes.func,
	onFill: PropTypes.func,
	success: PropTypes.bool,
	error: PropTypes.bool,
	disabled: PropTypes.bool,
	autoFocus: PropTypes.bool,
};

export default PinCodeInput;
